import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, brl } from "../../lib/api";
import {
  Flame, MessageSquare, CheckCircle2, XCircle, Users, AlertTriangle, Search,
  RefreshCw, ChevronRight, MapPin, HelpCircle, Clock,
} from "lucide-react";
import {
  IntentBadge, StatusBadge, SOURCE_LABEL, BU_LABEL, relativeTime,
} from "../../components/OpportunityBadges";

const PERIODS = [
  { key: "today", label: "Hoje" },
  { key: "7d", label: "7 dias" },
  { key: "30d", label: "30 dias" },
  { key: "all", label: "Tudo" },
];
const AGING_LABELS = { lt_30m: "< 30 min", "30m_2h": "30 min–2h", "2h_6h": "2h–6h", "6h_24h": "6h–24h", gt_24h: "> 24h" };

function Kpi({ icon: Icon, label, value, cls, testid, onClick }) {
  return (
    <button type="button" onClick={onClick} disabled={!onClick} data-testid={testid}
      className={`rounded-2xl border border-border bg-white p-3 text-left shadow-card ${onClick ? "hover:border-brand/40" : "cursor-default"}`}>
      <div className={`grid h-8 w-8 place-items-center rounded-lg ${cls}`}><Icon size={16} /></div>
      <div className="mt-1.5 text-xl font-bold text-brand-dark tabular-nums">{value ?? 0}</div>
      <div className="text-[10px] font-medium uppercase tracking-wide text-[#8A7A88]">{label}</div>
    </button>
  );
}

export default function AdminOpportunities() {
  const nav = useNavigate();
  const [tab, setTab] = useState("dashboard");
  const [period, setPeriod] = useState("30d");
  const [bu, setBu] = useState("");
  const [dash, setDash] = useState(null);
  const [radar, setRadar] = useState([]);
  const [byReseller, setByReseller] = useState([]);
  const [byStatus, setByStatus] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [savingCfg, setSavingCfg] = useState(false);
  const [savingRollout, setSavingRollout] = useState(false);
  const [resellers, setResellers] = useState([]);
  const [pilot, setPilot] = useState(null);
  const [preflight, setPreflight] = useState(null);
  const [preflightLoading, setPreflightLoading] = useState(false);
  const [feedback, setFeedback] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  // filtros da lista
  const [fStatus, setFStatus] = useState("");
  const [fAssigned, setFAssigned] = useState(""); // "", "unassigned", "ambiguous"
  const [q, setQ] = useState("");

  const loadDash = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const params = { period };
      if (bu) params.business_unit = bu;
      const [d, r, br, bs] = await Promise.all([
        api.get("/opportunities/admin/dashboard", { params }),
        api.get("/opportunities/admin/ddd-radar").catch(() => ({ data: [] })),
        api.get("/opportunities/admin/by-reseller", { params }).catch(() => ({ data: [] })),
        api.get("/opportunities/admin/by-whatsapp-status", { params }).catch(() => ({ data: [] })),
      ]);
      setDash(d.data); setRadar(r.data || []); setByReseller(br.data || []); setByStatus(bs.data || []);
    } catch { setError(true); } finally { setLoading(false); }
  }, [period, bu]);

  const loadList = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const params = {};
      if (fStatus) params.status = fStatus;
      if (bu) params.business_unit = bu;
      if (q.trim()) params.q = q.trim();
      const r = await api.get("/opportunities", { params });
      let data = r.data || [];
      if (fAssigned === "unassigned") data = data.filter((o) => !o.reseller_id);
      if (fAssigned === "ambiguous") data = data.filter((o) => o.customer_match_ambiguous);
      setRows(data);
    } catch { setError(true); } finally { setLoading(false); }
  }, [fStatus, fAssigned, bu, q]);

  const loadPilot = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const [p, f] = await Promise.all([
        api.get("/opportunities/admin/pilot", { params: { period } }),
        api.get("/opportunities/admin/classification-feedback").catch(() => ({ data: [] })),
      ]);
      setPilot(p.data); setFeedback(f.data || []);
    } catch { setError(true); } finally { setLoading(false); }
  }, [period]);

  useEffect(() => {
    if (tab === "dashboard") loadDash();
    else if (tab === "config") {
      api.get("/opportunities/config").then((r) => setCfg(r.data)).catch(() => {});
      api.get("/users/resellers").then((r) => setResellers(r.data || [])).catch(() => {});
    } else if (tab === "pilot") loadPilot();
    else loadList();
  }, [tab, loadDash, loadList, loadPilot]);

  const saveCfg = async () => {
    setSavingCfg(true);
    try {
      const r = await api.put("/opportunities/config", cfg);
      setCfg(r.data);
      toast.success("Configurações de SLA salvas");
    } catch { toast.error("Falha ao salvar"); } finally { setSavingCfg(false); }
  };
  const setC = (k, v) => setCfg((p) => ({ ...p, [k]: v }));

  const applyRollout = async () => {
    setSavingRollout(true);
    try {
      const r = await api.post("/opportunities/admin/rollout", {
        rollout_mode: cfg.rollout_mode, shadow_mode: !!cfg.shadow_mode,
        pilot_reseller_ids: cfg.pilot_reseller_ids || [],
      });
      setCfg(r.data);
      toast.success(`Rollout aplicado: ${r.data.rollout_mode}${r.data.shadow_mode ? " (shadow)" : ""}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha ao aplicar rollout"); }
    finally { setSavingRollout(false); }
  };

  const killSwitch = async () => {
    if (!window.confirm("DESATIVAR OPORTUNIDADES agora? Isso força rollout OFF e para novos efeitos do módulo imediatamente (o WhatsApp principal continua funcionando; nenhum dado é apagado).")) return;
    try {
      await api.post("/opportunities/admin/kill-switch");
      setCfg((p) => ({ ...p, rollout_mode: "OFF" }));
      toast.success("Kill switch acionado — rollout OFF");
    } catch { toast.error("Falha ao acionar kill switch"); }
  };

  const runPreflight = async (openaiTest = false) => {
    setPreflightLoading(true);
    try {
      const r = await api.get("/opportunities/admin/preflight", { params: { openai_test: openaiTest } });
      setPreflight(r.data);
    } catch { toast.error("Falha no preflight"); } finally { setPreflightLoading(false); }
  };

  const runWeekly = async () => {
    try {
      const r = await api.post("/opportunities/admin/pilot/weekly-summary/run");
      if (r.data.status === "skipped_off") toast.info("Resumo não gerado: rollout está OFF");
      else if (r.data.status === "sent") toast.success("Resumo semanal enviado ao Admin (sino)");
      else toast.error("Falha ao gerar resumo");
    } catch { toast.error("Falha ao gerar resumo"); }
  };

  const togglePilotId = (id) => setCfg((p) => {
    const cur = p.pilot_reseller_ids || [];
    return { ...p, pilot_reseller_ids: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] };
  });

  const conv = dash?.conversion_rate ?? 0;

  return (
    <div className="mx-auto max-w-6xl px-1 pb-24 sm:px-0" data-testid="admin-opportunities-page">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-brand-dark">
            <Flame className="text-brand" size={24} /> Oportunidades — Central
          </h1>
          <p className="mt-0.5 text-sm text-[#6B5A69]">Torre de controle comercial: funil, leads sem responsável e distribuição.</p>
        </div>
        <button onClick={() => (tab === "dashboard" ? loadDash() : tab === "pilot" ? loadPilot() : loadList())} data-testid="admin-opp-refresh"
          aria-label="Atualizar" className="grid h-10 w-10 place-items-center rounded-xl border border-border bg-white text-brand hover:bg-muted">
          <RefreshCw size={18} />
        </button>
      </div>

      {/* Tabs */}
      <div className="mb-3 flex gap-2 overflow-x-auto" data-testid="admin-opp-tabs">
        {[["dashboard", "Dashboard"], ["list", "Lista"], ["resellers", "Por revendedora"], ["radar", "Radar DDD"], ["pilot", "Saúde do Piloto"], ["config", "Configurações"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`admin-opp-tab-${k}`}
            className={`shrink-0 rounded-full px-4 py-1.5 text-sm font-semibold ${tab === k ? "bg-brand-grad text-white" : "border border-border bg-white text-[#6B5A69] hover:bg-muted"}`}>
            {l}
          </button>
        ))}
      </div>

      {/* Filtros globais (periodo + unidade) */}
      <div className="mb-4 flex flex-wrap gap-2">
        {PERIODS.map((p) => (
          <button key={p.key} onClick={() => setPeriod(p.key)} data-testid={`admin-opp-period-${p.key}`}
            className={`rounded-full px-3 py-1 text-xs font-semibold ${period === p.key ? "bg-brand text-white" : "border border-border bg-white text-[#6B5A69]"}`}>
            {p.label}
          </button>
        ))}
        <select value={bu} onChange={(e) => setBu(e.target.value)} data-testid="admin-opp-bu"
          className="rounded-full border border-border bg-white px-3 py-1 text-xs font-semibold text-[#6B5A69]">
          <option value="">Todos os segmentos</option>
          <option value="CRIS">Cris Lingerie</option>
          <option value="ELETROS">Eletros</option>
          <option value="UNKNOWN">Segmento não identificado</option>
        </select>
      </div>

      {error && (
        <div className="mb-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700" data-testid="admin-opp-error">
          Erro ao carregar dados. <button onClick={() => (tab === "dashboard" ? loadDash() : tab === "pilot" ? loadPilot() : loadList())} className="font-semibold underline">Tentar novamente</button>
        </div>
      )}

      {/* ===== DASHBOARD ===== */}
      {tab === "dashboard" && (
        <div data-testid="admin-opp-dashboard">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
            <Kpi icon={MessageSquare} label="Recebidas" value={dash?.total} cls="bg-brand/10 text-brand" testid="admin-kpi-total" />
            <Kpi icon={Flame} label="Novas" value={dash?.new} cls="bg-brand/10 text-brand" testid="admin-kpi-new" />
            <Kpi icon={MessageSquare} label="Em atend." value={dash?.contacting} cls="bg-amber-100 text-amber-700" testid="admin-kpi-contacting" />
            <Kpi icon={CheckCircle2} label="Convertidas" value={dash?.converted} cls="bg-emerald-100 text-emerald-700" testid="admin-kpi-converted" />
            <Kpi icon={XCircle} label="Não conv." value={dash?.lost} cls="bg-red-100 text-red-700" testid="admin-kpi-lost" />
            <Kpi icon={AlertTriangle} label="Sem dono" value={dash?.unassigned} cls="bg-orange-100 text-orange-700" testid="admin-kpi-unassigned"
              onClick={() => { setTab("list"); setFAssigned("unassigned"); }} />
            <Kpi icon={HelpCircle} label="Ambíguos" value={dash?.ambiguous} cls="bg-purple-100 text-purple-700" testid="admin-kpi-ambiguous"
              onClick={() => { setTab("list"); setFAssigned("ambiguous"); }} />
          </div>

          {/* SLA / saude (OP-8) */}
          <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="admin-opp-sla-kpis">
            <Kpi icon={Clock} label="Aguardando atenção" value={dash?.awaiting_attention} cls="bg-amber-100 text-amber-700" testid="admin-kpi-awaiting" />
            <Kpi icon={AlertTriangle} label="SLA vencido" value={dash?.sla_overdue} cls="bg-red-100 text-red-700" testid="admin-kpi-overdue" />
            <Kpi icon={Clock} label="Médio p/ visualizar" value={dash?.avg_view_minutes != null ? `${dash.avg_view_minutes}m` : "—"} cls="bg-muted text-[#6B5A69]" testid="admin-kpi-avgview" />
            <Kpi icon={Clock} label="Médio p/ atender" value={dash?.avg_contact_minutes != null ? `${dash.avg_contact_minutes}m` : "—"} cls="bg-muted text-[#6B5A69]" testid="admin-kpi-avgcontact" />
          </div>

          {/* Valor comercial atribuido (OP-7) */}
          <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3" data-testid="admin-opp-value-cards">
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 shadow-card" data-testid="admin-value-attributed">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700">Valor vendido atribuído às oportunidades</div>
              <div className="mt-1 text-2xl font-bold text-emerald-800 tabular-nums">{brl(dash?.attributed_value || 0)}</div>
              <div className="text-[10px] text-emerald-600">Vendas vinculadas, não canceladas · não é valor recebido</div>
            </div>
            <div className="rounded-2xl border border-border bg-white p-4 shadow-card" data-testid="admin-value-count">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-[#8A7A88]">Vendas atribuídas</div>
              <div className="mt-1 text-2xl font-bold text-brand-dark tabular-nums">{dash?.attributed_sales_count || 0}</div>
            </div>
            <div className="rounded-2xl border border-border bg-white p-4 shadow-card" data-testid="admin-value-ticket">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-[#8A7A88]">Ticket médio das vendas atribuídas</div>
              <div className="mt-1 text-2xl font-bold text-brand-dark tabular-nums">{brl(dash?.avg_ticket || 0)}</div>
            </div>
          </div>

          {/* Top 5 Status por R$ vendido (OP-8, leitura da atribuicao OP-7) */}
          <div className="mt-3 rounded-2xl border border-amber-200 bg-amber-50/60 p-4 shadow-card" data-testid="admin-opp-top-status">
            <h3 className="text-sm font-bold text-brand-dark">🏆 Status que mais geraram vendas</h3>
            {byStatus.length === 0 ? (
              <p className="mt-1 text-sm text-[#8A7A88]">Ainda não há vendas atribuídas a Status.</p>
            ) : (
              <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {byStatus.slice(0, 5).map((s, i) => (
                  <div key={s.source_status_id} className="rounded-xl border border-border bg-white p-3" data-testid={`admin-top-status-${i}`}>
                    <div className="truncate text-sm font-semibold text-brand-dark">{["🥇","🥈","🥉","4º","5º"][i]} {s.label}</div>
                    <div className="mt-1 text-lg font-bold text-emerald-700 tabular-nums">{brl(s.attributed_value || 0)}</div>
                    <div className="text-[11px] text-[#8A7A88]">{s.total} oport. · {s.converted} vendas</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            {/* Conversao + funil */}
            <div className="rounded-2xl border border-border bg-white p-5 shadow-card" data-testid="admin-opp-funnel">
              <div className="flex items-baseline justify-between">
                <h3 className="text-sm font-bold text-brand-dark">Funil</h3>
                <span className="text-xs text-[#8A7A88]">Conversão <b className="text-emerald-600">{conv}%</b></span>
              </div>
              <p className="mt-0.5 text-[10px] text-[#B4A6B2]">{dash?.conversion_formula}</p>
              <div className="mt-3 space-y-2">
                {[["Recebidas", dash?.funnel?.received], ["Visualizadas", dash?.funnel?.viewed], ["Em atendimento", dash?.funnel?.contacting], ["Convertidas", dash?.funnel?.converted]].map(([l, v], i) => {
                  const max = dash?.funnel?.received || 1;
                  const pct = Math.round(((v || 0) / max) * 100);
                  return (
                    <div key={i}>
                      <div className="flex justify-between text-xs text-[#6B5A69]"><span>{l}</span><span className="tabular-nums">{v ?? 0}</span></div>
                      <div className="mt-1 h-2 rounded-full bg-muted"><div className="h-2 rounded-full bg-brand-grad" style={{ width: `${pct}%` }} /></div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
                <div className="rounded-lg bg-muted p-2"><div className="text-[#8A7A88]">Tempo médio até visualizar</div><div className="font-bold text-brand-dark">{dash?.avg_view_minutes != null ? `${dash.avg_view_minutes} min` : "—"}</div></div>
                <div className="rounded-lg bg-muted p-2"><div className="text-[#8A7A88]">Tempo médio até atendimento</div><div className="font-bold text-brand-dark">{dash?.avg_contact_minutes != null ? `${dash.avg_contact_minutes} min` : "—"}</div></div>
              </div>
            </div>

            {/* Aging */}
            <div className="rounded-2xl border border-border bg-white p-5 shadow-card" data-testid="admin-opp-aging">
              <h3 className="flex items-center gap-1.5 text-sm font-bold text-brand-dark"><Clock size={15} className="text-brand" /> Idade dos leads abertos</h3>
              <div className="mt-3 space-y-2">
                {Object.entries(AGING_LABELS).map(([k, l]) => (
                  <div key={k} className="flex items-center justify-between text-sm">
                    <span className="text-[#6B5A69]">{l}</span>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-bold tabular-nums ${k === "gt_24h" && (dash?.aging?.[k] || 0) > 0 ? "bg-red-100 text-red-700" : "bg-muted text-[#6B5A69]"}`}>{dash?.aging?.[k] ?? 0}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Origem */}
            <div className="rounded-2xl border border-border bg-white p-5 shadow-card" data-testid="admin-opp-sources">
              <h3 className="text-sm font-bold text-brand-dark">Por origem</h3>
              <table className="mt-2 w-full text-sm">
                <thead><tr className="text-left text-[10px] uppercase text-[#8A7A88]"><th className="pb-1">Origem</th><th className="pb-1 text-right">Total</th><th className="pb-1 text-right">Conv.</th><th className="pb-1 text-right">Valor</th></tr></thead>
                <tbody>
                  {(dash?.by_source || []).map((s) => (
                    <tr key={s.source} className="border-t border-border/50"><td className="py-1">{SOURCE_LABEL[s.source] || s.source}</td><td className="py-1 text-right tabular-nums">{s.total}</td><td className="py-1 text-right tabular-nums text-emerald-600">{s.converted}</td><td className="py-1 text-right tabular-nums">{brl(s.attributed_value || 0)}</td></tr>
                  ))}
                  {(dash?.by_source || []).length === 0 && <tr><td colSpan={4} className="py-2 text-center text-[#8A7A88]">Sem dados</td></tr>}
                </tbody>
              </table>
            </div>

            {/* Por Status do WhatsApp (fecha o ciclo) */}
            <div className="rounded-2xl border border-border bg-white p-5 shadow-card lg:col-span-2" data-testid="admin-opp-by-status">
              <h3 className="text-sm font-bold text-brand-dark">Status do WhatsApp que geraram vendas</h3>
              {byStatus.length === 0 ? (
                <p className="mt-2 text-sm text-[#8A7A88]">Sem dados suficientes de Status com identificador estável neste período.</p>
              ) : (
                <div className="mt-2 overflow-x-auto">
                  <table className="w-full min-w-[520px] text-sm">
                    <thead><tr className="text-left text-[10px] uppercase text-[#8A7A88]"><th className="pb-1">Status</th><th className="pb-1 text-right">Oport.</th><th className="pb-1 text-right">🔥</th><th className="pb-1 text-right">🟡</th><th className="pb-1 text-right">Conv.</th><th className="pb-1 text-right">Valor atribuído</th></tr></thead>
                    <tbody>
                      {byStatus.map((s) => (
                        <tr key={s.source_status_id} className="border-t border-border/50" data-testid={`admin-status-${s.source_status_id}`}>
                          <td className="py-1 max-w-[220px] truncate">{s.label}</td>
                          <td className="py-1 text-right tabular-nums">{s.total}</td>
                          <td className="py-1 text-right tabular-nums">{s.high}</td>
                          <td className="py-1 text-right tabular-nums">{s.medium}</td>
                          <td className="py-1 text-right tabular-nums text-emerald-600">{s.converted}</td>
                          <td className="py-1 text-right tabular-nums font-semibold">{brl(s.attributed_value || 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Top produtos */}
            <div className="rounded-2xl border border-border bg-white p-5 shadow-card" data-testid="admin-opp-products">
              <h3 className="text-sm font-bold text-brand-dark">Interesses mais frequentes</h3>
              <ul className="mt-2 space-y-1.5 text-sm">
                {(dash?.top_products || []).map((p, i) => (
                  <li key={i} className="flex items-center justify-between">
                    <span className="truncate text-[#4A3A48]">{p.label}</span>
                    <span className="ml-2 shrink-0 text-xs text-[#8A7A88]">{p.total} · 🔥{p.high} · ✅{p.converted}</span>
                  </li>
                ))}
                {(dash?.top_products || []).length === 0 && <li className="text-[#8A7A88]">Sem dados</li>}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* ===== LISTA ===== */}
      {tab === "list" && (
        <div data-testid="admin-opp-list-tab">
          <div className="mb-3 space-y-2">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A7A88]" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por cliente/telefone/interesse…"
                data-testid="admin-opp-search" className="w-full rounded-xl border border-border bg-white py-2.5 pl-9 pr-3 text-sm focus:border-brand focus:outline-none" />
            </div>
            <div className="flex flex-wrap gap-2">
              {[["", "Todas"], ["NEW", "Novas"], ["CONTACTING", "Em atendimento"], ["CONVERTED", "Convertidas"], ["LOST", "Não convertidas"]].map(([k, l]) => (
                <button key={k || "all"} onClick={() => setFStatus(k)} data-testid={`admin-opp-fstatus-${k || "all"}`}
                  className={`rounded-full px-3 py-1 text-xs font-semibold ${fStatus === k ? "bg-brand-grad text-white" : "border border-border bg-white text-[#6B5A69]"}`}>{l}</button>
              ))}
              {[["", "Todos"], ["unassigned", "⚠️ Sem responsável"], ["ambiguous", "⚠️ Ambíguos"]].map(([k, l]) => (
                <button key={k || "any"} onClick={() => setFAssigned(k)} data-testid={`admin-opp-fassigned-${k || "any"}`}
                  className={`rounded-full px-3 py-1 text-xs font-semibold ${fAssigned === k ? "bg-orange-500 text-white" : "border border-border bg-white text-[#6B5A69]"}`}>{l}</button>
              ))}
            </div>
          </div>
          {loading ? (
            <div className="space-y-2">{[0, 1, 2].map((i) => <div key={i} className="h-20 animate-pulse rounded-xl bg-muted" />)}</div>
          ) : rows.length === 0 ? (
            <div className="rounded-2xl border border-border bg-white p-10 text-center text-sm text-[#6B5A69] shadow-card" data-testid="admin-opp-empty">
              {fAssigned === "unassigned" ? "Nenhum lead sem responsável 🎉" : "Nenhuma oportunidade encontrada neste período."}
            </div>
          ) : (
            <div className="space-y-2" data-testid="admin-opp-rows">
              {rows.map((o) => (
                <button key={o.id} onClick={() => nav(`/admin/oportunidades/${o.id}`)} data-testid={`admin-opp-row-${o.id}`}
                  className="flex w-full items-center gap-3 rounded-xl border border-border bg-white p-3 text-left shadow-card hover:border-brand/40">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate font-semibold text-brand-dark">{o.customer_name || "Sem nome"}</span>
                      {!o.reseller_id && <span className="rounded-full bg-orange-100 px-1.5 py-0.5 text-[10px] font-bold text-orange-700">SEM DONO</span>}
                      {o.customer_match_ambiguous && <span className="rounded-full bg-purple-100 px-1.5 py-0.5 text-[10px] font-bold text-purple-700">AMBÍGUO</span>}
                    </div>
                    <div className="mt-0.5 truncate text-xs text-[#6B5A69]">{o.product_text || o.original_message || "—"}</div>
                    <div className="mt-1 flex flex-wrap items-center gap-1.5">
                      <IntentBadge level={o.intent_level} />
                      <span className="text-[10px] text-[#8A7A88]">{SOURCE_LABEL[o.source] || o.source} · {BU_LABEL[o.business_unit] || o.business_unit} · {relativeTime(o.created_at)}</span>
                    </div>
                  </div>
                  <StatusBadge status={o.status} />
                  <ChevronRight size={16} className="shrink-0 text-[#B4A6B2]" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ===== POR REVENDEDORA ===== */}
      {tab === "resellers" && (
        <div className="overflow-x-auto rounded-2xl border border-border bg-white shadow-card" data-testid="admin-opp-by-reseller">
          <table className="w-full min-w-[640px] text-sm">
            <thead><tr className="bg-muted text-left text-[10px] uppercase text-[#8A7A88]">
              <th className="p-2">Revendedora</th><th className="p-2 text-right">Receb.</th><th className="p-2 text-right">Em atend.</th>
              <th className="p-2 text-right">Convert.</th><th className="p-2 text-right">Perdidas</th><th className="p-2 text-right">Conv.%</th>
              <th className="p-2 text-right">Vendas atrib.</th><th className="p-2 text-right">Valor atrib.</th><th className="p-2 text-right">T. médio</th>
            </tr></thead>
            <tbody>
              {byReseller.map((r) => (
                <tr key={r.reseller_id} className="border-t border-border/50" data-testid={`admin-opp-reseller-${r.reseller_id}`}>
                  <td className="p-2 font-medium text-brand-dark">{r.reseller_name}</td>
                  <td className="p-2 text-right tabular-nums">{r.received}</td>
                  <td className="p-2 text-right tabular-nums">{r.contacting}</td>
                  <td className="p-2 text-right tabular-nums text-emerald-600">{r.converted}</td>
                  <td className="p-2 text-right tabular-nums text-red-500">{r.lost}</td>
                  <td className="p-2 text-right tabular-nums">{r.conversion_rate}%</td>
                  <td className="p-2 text-right tabular-nums">{r.attributed_sales || 0}</td>
                  <td className="p-2 text-right tabular-nums font-semibold">{brl(r.attributed_value || 0)}</td>
                  <td className="p-2 text-right tabular-nums">{r.avg_contact_minutes != null ? `${r.avg_contact_minutes}m` : "—"}</td>
                </tr>
              ))}
              {byReseller.length === 0 && <tr><td colSpan={9} className="p-4 text-center text-[#8A7A88]">Sem dados no período.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* ===== RADAR DDD ===== */}
      {tab === "radar" && (
        <div data-testid="admin-opp-radar">
          <div className="mb-2 flex items-center gap-1.5 text-sm text-[#6B5A69]"><MapPin size={15} className="text-brand" /> Leads sem responsável por DDD e segmento (dados agregados, sem localização individual).</div>
          {radar.length === 0 ? (
            <div className="rounded-2xl border border-border bg-white p-10 text-center text-sm text-[#6B5A69] shadow-card">Nenhum lead sem responsável 🎉</div>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {radar.map((d) => (
                <div key={d.ddd} className="rounded-2xl border border-border bg-white p-4 shadow-card" data-testid={`admin-opp-radar-${d.ddd}`}>
                  <div className="flex items-baseline justify-between"><span className="text-lg font-bold text-brand-dark">DDD {d.ddd}</span><span className="text-xs font-bold text-orange-600">{d.total}</span></div>
                  <div className="mt-2 space-y-1 text-xs text-[#6B5A69]">
                    <div className="flex justify-between"><span>Cris</span><span className="tabular-nums">{d.CRIS || 0}</span></div>
                    <div className="flex justify-between"><span>Eletros</span><span className="tabular-nums">{d.ELETROS || 0}</span></div>
                    <div className="flex justify-between"><span>Não identif.</span><span className="tabular-nums">{d.UNKNOWN || 0}</span></div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {/* ===== CONFIG SLA ===== */}
      {tab === "pilot" && (
        <div className="space-y-4" data-testid="admin-opp-pilot">
          {!pilot ? (
            <div className="h-40 animate-pulse rounded-2xl bg-muted" />
          ) : (
            <>
              {/* estado do rollout */}
              <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-border bg-white p-4 shadow-card">
                <span className="text-sm font-bold text-brand-dark">Rollout:</span>
                <span data-testid="pilot-mode" className={`rounded-full px-3 py-1 text-xs font-bold ${pilot.rollout.mode === "ALL" ? "bg-emerald-100 text-emerald-700" : pilot.rollout.mode === "PILOT" ? "bg-amber-100 text-amber-700" : "bg-gray-200 text-gray-600"}`}>
                  {pilot.rollout.mode}{pilot.rollout.shadow_mode ? " • shadow" : ""}
                </span>
                <span className="text-xs text-[#6B5A69]">{(pilot.rollout.pilot_resellers || []).length} revendedora(s) piloto</span>
                <div className="ml-auto flex gap-2">
                  <button onClick={() => runPreflight(false)} data-testid="pilot-run-preflight" className="rounded-xl border border-border bg-white px-3 py-1.5 text-xs font-semibold text-brand hover:bg-muted">Rodar preflight</button>
                  <button onClick={runWeekly} data-testid="pilot-run-weekly" className="rounded-xl border border-border bg-white px-3 py-1.5 text-xs font-semibold text-brand hover:bg-muted">Enviar resumo semanal</button>
                </div>
              </div>

              {/* KPIs do piloto */}
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
                {[["Mensagens", pilot.messages_processed], ["Oportunidades", pilot.opportunities_created],
                  ["Conversões", pilot.conversions], ["Sem responsável", pilot.leads_without_owner],
                  ["Respostas enviadas", pilot.effects.replies_sent], ["Notificações", pilot.effects.notifications_sent]].map(([l, v]) => (
                  <div key={l} className="rounded-2xl border border-border bg-white p-3 shadow-card">
                    <div className="text-xl font-bold text-brand-dark tabular-nums">{v ?? 0}</div>
                    <div className="text-[10px] font-medium uppercase tracking-wide text-[#8A7A88]">{l}</div>
                  </div>
                ))}
              </div>
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 shadow-card" data-testid="pilot-value">
                <div className="text-xs font-semibold text-emerald-700">R$ vendido atribuído (piloto)</div>
                <div className="mt-1 text-2xl font-bold text-emerald-800 tabular-nums">{brl(pilot.attributed_value || 0)}</div>
              </div>

              <div className="grid gap-4 lg:grid-cols-2">
                {/* IA */}
                <div className="rounded-2xl border border-border bg-white p-5 shadow-card" data-testid="pilot-ai">
                  <h3 className="text-sm font-bold text-brand-dark">Qualidade da IA (classificação)</h3>
                  <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                    <div><div className="text-xs text-[#8A7A88]">Classificadas</div><div className="font-bold tabular-nums">{pilot.ai.total_classified}</div></div>
                    <div><div className="text-xs text-[#8A7A88]">Latência média</div><div className="font-bold tabular-nums">{pilot.ai.avg_latency_ms != null ? `${pilot.ai.avg_latency_ms}ms` : "—"}</div></div>
                    <div><div className="text-xs text-[#8A7A88]">% OpenAI</div><div className="font-bold tabular-nums text-emerald-600">{pilot.ai.openai_pct}%</div></div>
                    <div><div className="text-xs text-[#8A7A88]">% Fallback</div><div className="font-bold tabular-nums text-amber-600">{pilot.ai.fallback_pct}%</div></div>
                  </div>
                  <div className="mt-3 border-t border-border/50 pt-2 text-xs text-[#6B5A69]">
                    {Object.entries(pilot.by_route || {}).map(([k, v]) => (
                      <div key={k} className="flex justify-between py-0.5"><span>{k}</span><span className="tabular-nums">{v}</span></div>
                    ))}
                  </div>
                </div>

                {/* Simulação (WOULD) + Intenção */}
                <div className="rounded-2xl border border-border bg-white p-5 shadow-card" data-testid="pilot-would">
                  <h3 className="text-sm font-bold text-brand-dark">Simulação (o que aconteceria)</h3>
                  <div className="mt-3 grid grid-cols-3 gap-3 text-sm">
                    <div><div className="text-xs text-[#8A7A88]">Responderia</div><div className="font-bold tabular-nums">{pilot.would.reply_customer}</div></div>
                    <div><div className="text-xs text-[#8A7A88]">Notificaria</div><div className="font-bold tabular-nums">{pilot.would.notify_reseller}</div></div>
                    <div><div className="text-xs text-[#8A7A88]">SLA</div><div className="font-bold tabular-nums">{pilot.would.trigger_sla}</div></div>
                  </div>
                  <h3 className="mt-4 text-sm font-bold text-brand-dark">Nível de intenção</h3>
                  <div className="mt-2 grid grid-cols-3 gap-3 text-sm">
                    <div><div className="text-xs text-[#8A7A88]">HIGH 🔥</div><div className="font-bold tabular-nums">{pilot.by_intent_level.HIGH}</div></div>
                    <div><div className="text-xs text-[#8A7A88]">MEDIUM 🟡</div><div className="font-bold tabular-nums">{pilot.by_intent_level.MEDIUM}</div></div>
                    <div><div className="text-xs text-[#8A7A88]">LOW 🔵</div><div className="font-bold tabular-nums">{pilot.by_intent_level.LOW}</div></div>
                  </div>
                </div>
              </div>

              {/* Erros */}
              <div className="rounded-2xl border border-border bg-white p-5 shadow-card" data-testid="pilot-errors">
                <h3 className="text-sm font-bold text-brand-dark">Log de erros (contadores)</h3>
                <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
                  {Object.entries(pilot.errors || {}).map(([k, v]) => (
                    <div key={k} className={`rounded-xl border px-3 py-2 ${v > 0 ? "border-red-200 bg-red-50" : "border-border bg-white"}`}>
                      <div className={`text-lg font-bold tabular-nums ${v > 0 ? "text-red-600" : "text-brand-dark"}`}>{v}</div>
                      <div className="text-[10px] uppercase text-[#8A7A88]">{k.replace(/_/g, " ")}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Preflight resultado */}
              {preflight && (
                <div className="rounded-2xl border border-border bg-white p-5 shadow-card" data-testid="pilot-preflight-result">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-bold text-brand-dark">Preflight de produção</h3>
                    <div className="flex items-center gap-2">
                      <span className={`rounded-full px-3 py-1 text-xs font-bold ${preflight.ready ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>{preflight.ready ? "PRONTO" : "NÃO PRONTO"}</span>
                      <button onClick={() => runPreflight(true)} disabled={preflightLoading} className="rounded-lg border border-border px-2 py-1 text-xs font-semibold text-brand disabled:opacity-50">Testar OpenAI real</button>
                    </div>
                  </div>
                  <div className="mt-3 space-y-1">
                    {preflight.checks.map((c) => (
                      <div key={c.name} className="flex items-center gap-2 text-xs">
                        <span className={`inline-block h-2 w-2 rounded-full ${c.status === "OK" ? "bg-emerald-500" : c.status === "WARN" ? "bg-amber-500" : "bg-red-500"}`} />
                        <span className="font-semibold text-[#6B5A69]">{c.name}</span>
                        <span className="text-[#8A7A88]">— {c.detail}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Feedback de classificação */}
              <div className="rounded-2xl border border-border bg-white p-5 shadow-card" data-testid="pilot-feedback">
                <h3 className="text-sm font-bold text-brand-dark">Feedback de classificação ({feedback.length})</h3>
                <p className="text-xs text-[#8A7A88]">Somente para avaliação do piloto — não altera o prompt/lógica automaticamente.</p>
                {feedback.length === 0 ? (
                  <div className="mt-2 text-xs text-[#8A7A88]">Nenhum feedback registrado.</div>
                ) : (
                  <div className="mt-2 max-h-60 space-y-1 overflow-y-auto">
                    {feedback.map((f) => (
                      <div key={f.id} className="flex flex-wrap items-center gap-2 rounded-lg border border-border/50 px-2 py-1.5 text-xs">
                        <span className={`rounded px-1.5 py-0.5 font-bold ${f.kind === "FALSE_NEGATIVE" ? "bg-red-100 text-red-700" : f.kind === "FALSE_POSITIVE" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>{f.kind}</span>
                        {f.expected_route && <span>→ {f.expected_route}</span>}
                        {f.expected_intent_level && <span>/ {f.expected_intent_level}</span>}
                        {f.note && <span className="text-[#8A7A88]">{f.note}</span>}
                        <span className="ml-auto text-[#8A7A88]">{relativeTime(f.created_at)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {tab === "config" && (
        <div className="max-w-2xl space-y-4" data-testid="admin-opp-config">
          {!cfg ? (
            <div className="h-40 animate-pulse rounded-2xl bg-muted" />
          ) : (
            <>
              {/* FASE PILOTO - Rollout controlado + kill switch */}
              <div className="rounded-2xl border-2 border-brand/30 bg-white p-5 shadow-card" data-testid="admin-opp-rollout">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-bold text-brand-dark">Rollout controlado (ativação em produção)</h3>
                    <p className="text-xs text-[#8A7A88]">Autoridade dos efeitos externos. OFF = nada. PILOT = só revendedoras piloto. ALL = todas.</p>
                  </div>
                  <span data-testid="rollout-current" className={`rounded-full px-3 py-1 text-xs font-bold ${cfg.rollout_mode === "ALL" ? "bg-emerald-100 text-emerald-700" : cfg.rollout_mode === "PILOT" ? "bg-amber-100 text-amber-700" : "bg-gray-200 text-gray-600"}`}>
                    {cfg.rollout_mode || "OFF"}{cfg.shadow_mode ? " • shadow" : ""}
                  </span>
                </div>
                <div className="mt-4 flex gap-2">
                  {["OFF", "PILOT", "ALL"].map((m) => (
                    <button key={m} onClick={() => setC("rollout_mode", m)} data-testid={`rollout-mode-${m}`}
                      className={`flex-1 rounded-xl px-3 py-2 text-sm font-bold ${cfg.rollout_mode === m ? "bg-brand-grad text-white" : "border border-border bg-white text-[#6B5A69]"}`}>{m}</button>
                  ))}
                </div>
                <label className="mt-3 flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={!!cfg.shadow_mode} onChange={(e) => setC("shadow_mode", e.target.checked)} data-testid="rollout-shadow" />
                  Modo shadow (processa tudo, mas <b>não</b> dispara efeito real — só registra o que aconteceria)
                </label>
                {cfg.rollout_mode === "PILOT" && (
                  <div className="mt-3">
                    <div className="text-xs font-semibold text-[#6B5A69]">Revendedoras piloto (allowlist)</div>
                    <div className="mt-1.5 max-h-44 overflow-y-auto rounded-xl border border-border p-2" data-testid="rollout-pilot-list">
                      {resellers.length === 0 ? (
                        <div className="p-2 text-xs text-[#8A7A88]">Nenhuma revendedora encontrada.</div>
                      ) : resellers.map((r) => (
                        <label key={r.id} className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-muted">
                          <input type="checkbox" checked={(cfg.pilot_reseller_ids || []).includes(r.id)}
                            onChange={() => togglePilotId(r.id)} data-testid={`rollout-pilot-${r.id}`} />
                          <span>{r.name}</span>
                          <span className="ml-auto text-xs text-[#8A7A88]">{r.email}</span>
                        </label>
                      ))}
                    </div>
                    <div className="mt-1 text-xs text-[#8A7A88]">{(cfg.pilot_reseller_ids || []).length} selecionada(s)</div>
                  </div>
                )}
                <div className="mt-4 flex flex-wrap gap-2">
                  <button onClick={applyRollout} disabled={savingRollout} data-testid="rollout-apply"
                    className="rounded-xl bg-brand-grad px-4 py-2 text-sm font-bold text-white disabled:opacity-50">
                    {savingRollout ? "Aplicando…" : "Aplicar rollout"}
                  </button>
                  <button onClick={killSwitch} data-testid="rollout-kill"
                    className="rounded-xl border-2 border-red-300 bg-red-50 px-4 py-2 text-sm font-bold text-red-700 hover:bg-red-100">
                    ⛔ Desativar oportunidades (kill switch)
                  </button>
                </div>
              </div>

              <div className="rounded-2xl border border-border bg-white p-5 shadow-card">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-bold text-brand-dark">Régua de SLA</h3>
                    <p className="text-xs text-[#8A7A88]">Lembretes internos para não deixar oportunidades esfriarem. Nunca envia mensagem à cliente.</p>
                  </div>
                  <label className="inline-flex cursor-pointer items-center gap-2">
                    <input type="checkbox" checked={!!cfg.sla_enabled} onChange={(e) => setC("sla_enabled", e.target.checked)} data-testid="cfg-sla-enabled" />
                    <span className="text-sm font-semibold">{cfg.sla_enabled ? "Ativo" : "Inativo"}</span>
                  </label>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-3">
                  {[["high_first_reminder_minutes", "1º lembrete HIGH (min)"], ["medium_first_reminder_minutes", "1º lembrete MEDIUM (min)"],
                    ["reminder_viewed_minutes", "Visualizada sem atender (min)"], ["reminder_contacting_hours", "Em atendimento parado (h)"],
                    ["max_reminders_per_opportunity", "Máx. lembretes"], ["reminder_cooldown_minutes", "Cooldown (min)"]].map(([k, l]) => (
                    <label key={k} className="text-xs font-medium text-[#6B5A69]">{l}
                      <input type="number" min={0} value={cfg[k] ?? 0} onChange={(e) => setC(k, Number(e.target.value))} data-testid={`cfg-${k}`}
                        className="mt-1 w-full rounded-xl border border-border bg-white px-3 py-2 text-sm text-brand-dark focus:border-brand focus:outline-none" />
                    </label>
                  ))}
                </div>
                <label className="mt-3 flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={!!cfg.sla_include_low_intent} onChange={(e) => setC("sla_include_low_intent", e.target.checked)} data-testid="cfg-low" />
                  Incluir intenção BAIXA na régua
                </label>
              </div>

              <div className="rounded-2xl border border-border bg-white p-5 shadow-card">
                <h3 className="text-sm font-bold text-brand-dark">Horário comercial</h3>
                <label className="mt-2 flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={!!cfg.business_hours_enabled} onChange={(e) => setC("business_hours_enabled", e.target.checked)} data-testid="cfg-bh-enabled" />
                  Respeitar janela comercial (não notificar de madrugada)
                </label>
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <label className="text-xs font-medium text-[#6B5A69]">Início (h)<input type="number" min={0} max={23} value={cfg.business_hours_start ?? 8} onChange={(e) => setC("business_hours_start", Number(e.target.value))} data-testid="cfg-bh-start" className="mt-1 w-full rounded-xl border border-border bg-white px-3 py-2 text-sm" /></label>
                  <label className="text-xs font-medium text-[#6B5A69]">Fim (h)<input type="number" min={1} max={24} value={cfg.business_hours_end ?? 21} onChange={(e) => setC("business_hours_end", Number(e.target.value))} data-testid="cfg-bh-end" className="mt-1 w-full rounded-xl border border-border bg-white px-3 py-2 text-sm" /></label>
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {["Seg","Ter","Qua","Qui","Sex","Sáb","Dom"].map((d, i) => {
                    const on = (cfg.business_days || []).includes(i);
                    return (
                      <button key={i} onClick={() => setC("business_days", on ? cfg.business_days.filter((x) => x !== i) : [...(cfg.business_days || []), i])}
                        data-testid={`cfg-day-${i}`} className={`rounded-full px-3 py-1 text-xs font-semibold ${on ? "bg-brand-grad text-white" : "border border-border bg-white text-[#6B5A69]"}`}>{d}</button>
                    );
                  })}
                </div>
              </div>

              <div className="rounded-2xl border border-border bg-white p-5 shadow-card">
                <h3 className="text-sm font-bold text-brand-dark">Escalonamento e canais</h3>
                <label className="mt-2 flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={!!cfg.admin_escalation_enabled} onChange={(e) => setC("admin_escalation_enabled", e.target.checked)} data-testid="cfg-esc-enabled" />
                  Alertar o admin quando oportunidade HIGH fica sem atendimento
                </label>
                <label className="mt-2 block text-xs font-medium text-[#6B5A69]">Alertar admin após (min)
                  <input type="number" min={0} value={cfg.admin_escalation_minutes ?? 180} onChange={(e) => setC("admin_escalation_minutes", Number(e.target.value))} data-testid="cfg-esc-min" className="mt-1 w-40 rounded-xl border border-border bg-white px-3 py-2 text-sm" />
                </label>
                <label className="mt-3 flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={!!cfg.sla_whatsapp_enabled} onChange={(e) => setC("sla_whatsapp_enabled", e.target.checked)} data-testid="cfg-sla-wa" />
                  Enviar lembrete de SLA também por WhatsApp à revendedora
                </label>
              </div>

              <button onClick={saveCfg} disabled={savingCfg} data-testid="cfg-save"
                className="w-full rounded-xl bg-brand-grad py-3 text-sm font-bold text-white disabled:opacity-50">
                {savingCfg ? "Salvando…" : "Salvar configurações"}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
